import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApi, Spinner, ErrBanner } from '../../hooks/useApi';
import { api } from '../../services/api';
import ConfirmModal from '../../components/ConfirmModal';
import { useAuth } from '../../context/AuthContext';

const EMPTY_USER = {
  name: '', email: '', password: '', phone: '',
  role_id: '', is_active: true, station_ids: [],
};

export default function UserSection() {
  const { t } = useTranslation();
  const { can, currentStationId } = useAuth();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_USER);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [reset, setReset] = useState(null);
  const [msg, setMsg] = useState('');
  const notify = (m) => { setMsg(m); setTimeout(() => setMsg(''), 3500); };

  const { data: users, loading, error, reload } = useApi(
    () => api.users.list(), []
  );
  const { data: roles } = useApi(() => api.roles.list(), [], []);
  const { data: stations } = useApi(() => api.stations.mine(), [], []);

  // Only Approved + active stations can be assigned.
  const activeStations = (stations || [])
    .filter(s => s.status === 'Approved' && Number(s.is_active) === 1);

  // Open the create form with the current station pre-selected (single-station
  // default keeps parity with the old behaviour).
  const openCreate = () => {
    const base = currentStationId ? [Number(currentStationId)] : [];
    setForm({ ...EMPTY_USER, station_ids: base });
    setEditId(null);
    setShowForm(true);
  };

  const toggleStation = (sid) => {
    setForm(f => {
      const set = new Set((f.station_ids || []).map(Number));
      if (set.has(sid)) set.delete(sid); else set.add(sid);
      return { ...f, station_ids: Array.from(set) };
    });
  };

  const startEdit = (u) => {
    setForm({
      name: u.name,
      email: u.email,
      password: '',
      phone: u.phone || '',
      role_id: u.role_id,
      is_active: !!Number(u.is_active),
      station_ids: (u.station_ids || []).map(Number),
    });
    setEditId(u.id);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.name.trim())  { notify('❌ ' + t('userManagement.section.errors.nameRequired')); return; }
    if (!form.email.trim()) { notify('❌ ' + t('userManagement.section.errors.emailRequired')); return; }
    if (!form.role_id)      { notify('❌ ' + t('userManagement.section.errors.selectRole')); return; }
    if (!(form.station_ids || []).length) { notify('❌ ' + t('userManagement.section.errors.selectStation')); return; }
    if (!editId && (!form.password || form.password.length < 6)) {
      notify('❌ ' + t('userManagement.section.errors.passwordMin')); return;
    }
    const stationIds = (form.station_ids || []).map(Number);
    setSaving(true);
    try {
      if (editId) {
        await api.users.update(editId, {
          name: form.name.trim(),
          phone: form.phone.trim(),
          role_id: parseInt(form.role_id),
          is_active: form.is_active ? 1 : 0,
          station_ids: stationIds,
        });
        notify('✅ ' + t('userManagement.section.toasts.userUpdated'));
      } else {
        // Backend decides the outcome (new sub-user / reuse existing user +
        // grant stations / owner→company member) and returns a `message`.
        const res = await api.users.create({
          name: form.name.trim(),
          email: form.email.trim().toLowerCase(),
          password: form.password,
          phone: form.phone.trim(),
          role_id: parseInt(form.role_id),
          station_ids: stationIds,
        });
        notify('✅ ' + (res?.message || t('userManagement.section.toasts.userCreated')));
      }
      setShowForm(false); setForm(EMPTY_USER); setEditId(null); reload();
    } catch (e) { notify('❌ ' + e.message); }
    setSaving(false);
  };

  const handleDelete = async () => {
    try {
      await api.users.remove(confirm.id);
      notify('✅ ' + t('userManagement.section.toasts.userDeactivated'));
      reload();
    } catch (e) { notify('❌ ' + e.message); }
    setConfirm(null);
  };

  const handleResetPassword = async () => {
    if (!reset.newPw || reset.newPw.length < 6) {
      notify('❌ ' + t('userManagement.section.errors.passwordMin')); return;
    }
    try {
      await api.users.resetPassword(reset.user.id, reset.newPw);
      notify('✅ ' + t('userManagement.section.toasts.passwordReset'));
      setReset(null);
    } catch (e) { notify('❌ ' + e.message); }
  };

  const assignableRoles = (roles || []).filter(r => Number(r.is_system) !== 1);

  return (
    <div className="user-section">
       <div className="section-header" style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
        <span>{t('userManagement.section.usersCount', { n: (users || []).length })}</span>
        {can('users', 'create') && (
          <button className="btn btn-primary btn-sm"
                  onClick={() => { if (showForm) { setShowForm(false); } else { openCreate(); } }}>
            {showForm ? '✕ ' + t('common.cancel') : '＋ ' + t('userManagement.section.newUser')}
          </button>
        )}
      </div>

      {msg && <div className="um-toast">{msg}</div>}
      {error && <ErrBanner msg={error} />}

      {showForm && (
        <div className="section-card" style={{marginTop:16}}>
          <div className="section-header">{editId ? t('userManagement.section.editUser') : t('userManagement.section.createNewUser')}</div>
          <div className="um-form-grid">
            <div className="form-group">
              <label>{t('userManagement.section.nameLabel')}</label>
              <input value={form.name}
                     onChange={e => setForm({...form, name: e.target.value})} />
            </div>
            <div className="form-group">
              <label>{t('userManagement.section.emailLabel')}</label>
              <input type="email" value={form.email} disabled={!!editId}
                     onChange={e => setForm({...form, email: e.target.value})} />
            </div>
            {!editId && (
              <div className="form-group">
                <label>{t('userManagement.section.passwordLabel')}</label>
                <input type="password" value={form.password}
                       onChange={e => setForm({...form, password: e.target.value})} />
              </div>
            )}
            <div className="form-group">
              <label>{t('userManagement.section.phoneLabel')}</label>
              <input value={form.phone}
                     onChange={e => setForm({...form, phone: e.target.value})} />
            </div>
            <div className="form-group">
              <label>{t('userManagement.section.roleLabel')}</label>
              <select value={form.role_id}
                      onChange={e => setForm({...form, role_id: e.target.value})}>
                <option value="">{t('userManagement.section.selectRole')}</option>
                {assignableRoles.map(r => (
                  <option key={r.id} value={r.id}>{r.role_name}</option>
                ))}
              </select>
            </div>
            {editId && (
              <div className="form-group" style={{display:'flex', alignItems:'center'}}>
                <label style={{display:'flex', alignItems:'center', gap:6, margin:0}}>
                  <input type="checkbox" checked={form.is_active}
                         onChange={e => setForm({...form, is_active: e.target.checked})} />
                  {t('userManagement.section.activeCanLogin')}
                </label>
              </div>
            )}
          </div>

          {/* Accessible Stations — grant this user access to one or more stations. */}
          <div className="form-group" style={{padding:'0 12px'}}>
            <label>{t('userManagement.section.accessibleStations')}</label>
            {activeStations.length === 0 ? (
              <div style={{color:'var(--text-muted)', fontSize:13}}>{t('userManagement.section.noActiveStations')}</div>
            ) : (
              <div style={{
                display:'grid',
                gridTemplateColumns:'repeat(auto-fill, minmax(180px, 1fr))',
                gap:8, marginTop:6,
              }}>
                {activeStations.map(s => {
                  const checked = (form.station_ids || []).map(Number).includes(Number(s.id));
                  return (
                    <label key={s.id}
                           style={{
                             display:'flex', alignItems:'center', gap:8,
                             padding:'8px 10px', border:'1px solid var(--border)',
                             borderRadius:6, cursor:'pointer',
                             background: checked ? 'var(--primary-light)' : 'transparent',
                           }}>
                      <input type="checkbox" checked={checked}
                             onChange={() => toggleStation(Number(s.id))} />
                      <span style={{fontSize:13}}>{s.name}</span>
                      {Number(s.is_base) === 1 && (
                        <span className="cs-badge cs-own" style={{marginLeft:'auto'}}>{t('userManagement.section.base')}</span>
                      )}
                    </label>
                  );
                })}
              </div>
            )}
            <div style={{fontSize:11.5, color:'var(--text-muted)', marginTop:6}}>
              {t('userManagement.section.stationsHint')}
            </div>
          </div>

          <div style={{padding:'0 12px 12px'}}>
            <button className="btn btn-primary btn-sm" disabled={saving} onClick={handleSave}>
              {saving ? '⏳ ' + t('common.saving') : (editId ? t('userManagement.section.updateUser') : t('userManagement.section.createUser'))}
            </button>
          </div>
        </div>
      )}

      {loading ? <Spinner /> : (
        <div className="section-card" style={{overflowX:'auto', marginTop:16}}>
          <table className="data-table">
            <thead>
              <tr>
                <th style={{textAlign:'left'}}>{t('common.name')}</th>
                <th style={{textAlign:'left'}}>{t('userManagement.section.thEmail')}</th>
                <th style={{textAlign:'left'}}>{t('userManagement.section.thRole')}</th>
                <th style={{textAlign:'left'}}>{t('userManagement.section.thStations')}</th>
                <th style={{textAlign:'left'}}>{t('userManagement.section.thPhone')}</th>
                <th>{t('common.status')}</th>
                <th style={{textAlign:'left'}}>{t('userManagement.section.thLastLogin')}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {(users || []).length === 0 ? (
                <tr><td colSpan="8" style={{textAlign:'center', color:'var(--text-muted)'}}>
                  {t('userManagement.section.noSubUsers')}
                </td></tr>
              ) : (users || []).map(u => (
                <tr key={u.id} style={{opacity: Number(u.is_active) === 0 ? 0.55 : 1}}>
                  <td style={{textAlign:'left', fontWeight:600}}>{u.name}</td>
                  <td style={{textAlign:'left'}}>{u.email}</td>
                  <td style={{textAlign:'left'}}>
                    <span className="um-role-pill">{u.role_name}</span>
                  </td>
                  <td style={{textAlign:'left'}}>
                    {(u.stations && u.stations.length)
                      ? (
                        <span style={{display:'flex', gap:4, flexWrap:'wrap'}}>
                          {u.stations.map(s => (
                            <span key={s.id} className="um-role-pill"
                                  style={{background:'var(--primary-light)'}}>{s.name}</span>
                          ))}
                        </span>
                      )
                      : <span style={{color:'var(--text-muted)'}}>—</span>}
                  </td>
                  <td style={{textAlign:'left'}}>{u.phone || '—'}</td>
                  <td>
                    {Number(u.is_active) === 1
                      ? <span className="um-status-active">{t('common.active')}</span>
                      : <span className="um-status-inactive">{t('common.inactive')}</span>}
                  </td>
                  <td style={{textAlign:'left', fontSize:11.5, color:'var(--text-muted)'}}>
                    {u.last_login || t('userManagement.section.never')}
                  </td>
                  <td style={{display:'flex', gap:4, flexWrap:'wrap'}}>
                    {can('users', 'update') && (
                      <button className="btn btn-sm" onClick={() => startEdit(u)}>✏️</button>
                    )}
                    {can('users', 'update') && (
                      <button className="btn btn-sm" title={t('userManagement.section.resetPasswordTitle')}
                              onClick={() => setReset({user: u, newPw: ''})}>🔑</button>
                    )}
                    {Number(u.is_active) === 1 && can('users', 'delete') && (
                      <button className="btn btn-sm" style={{color:'var(--danger)'}}
                              onClick={() => setConfirm(u)}>🗑</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmModal
        isOpen={!!confirm}
        title={t('userManagement.section.deactivateUserTitle')}
        message={confirm ? t('userManagement.section.deactivateUserMsg', { name: confirm.name }) : ''}
        onConfirm={handleDelete}
        onCancel={() => setConfirm(null)}
        confirmLabel={t('userManagement.section.deactivateConfirm')}
      />

      {reset && (
        <div className="um-modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) setReset(null); }}>
          <div className="um-modal">
            <h3>{t('userManagement.section.resetPasswordFor', { name: reset.user.name })}</h3>
            <p style={{color:'var(--text-secondary)', fontSize:13}}>
              {t('userManagement.section.resetPasswordNote')}
            </p>
            <input type="password" placeholder={t('userManagement.section.newPasswordPlaceholder')}
                   value={reset.newPw}
                   onChange={e => setReset({...reset, newPw: e.target.value})}
                   style={{width:'100%', padding:'8px 10px', border:'1px solid var(--border)', borderRadius:4, marginTop:10}} />
            <div style={{display:'flex', gap:8, justifyContent:'flex-end', marginTop:16}}>
              <button className="btn btn-sm" onClick={() => setReset(null)}>{t('common.cancel')}</button>
              <button className="btn btn-primary btn-sm" onClick={handleResetPassword}>{t('userManagement.section.reset')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
